import { FC } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import useViewMode from '@/hooks/use-view-mode.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import ReadonlyView from '../../form/readonly-view';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import { getPolicyById, patchPolicy } from '@/lib/services/policy.service';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import { Form } from '@/components/form';
import { FieldError } from '@/lib/services';
import { Box } from '@mui/material';
import { DrawerProps } from '../helpers';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';

interface Props extends DrawerProps {
  policyId: string;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Description', prop: 'description' },
];

const getReadonlyData = (policy: PolicyModel | null) => {
  if (!policy) {
    return {};
  }

  const { name, description } = policy;

  return {
    name,
    description,
  };
};

export const PolicyDrawer: FC<Props> = ({ policyId, open, onClose, className, isTopDrawer }) => {
  const { inWriteMode } = useViewMode('edit');
  const { toast, errorToast } = useToast();

  const query = useQuery([QueryKey.PolicyView, policyId], () => getPolicyById(policyId));
  const { data } = query;
  const policy = data || new PolicyModel();

  const handleRefresh = () => {
    refetchQuery(QueryKey.PolicyView);
  };

  const formHandle = useFormHandle(
    {
      initialValues: policy,
      validateOnChange: true,
      onSubmit: async (values, { resetForm, setFieldError }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        const { patched, error, fieldErrors } = await patchPolicy(
          policyId,
          values.propSlice(['name', 'description'])
        );

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !patched) {
          errorToast(error || 'The policy was not updated');
          return;
        }

        resetForm({ values });
        toast('The policy was updated');
        handleRefresh();
        setTimeout(() => {
          onClose();
        }, 500);
      },
    },
    { lockTarget: 'drawer' }
  );

  const { handleSubmit, canSubmit, isSubmitting } = formHandle;

  const handleCancel = () => {
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query}>
      <DrawerHeader onClose={onClose} isTopDrawer={isTopDrawer} hideMenu>
        {policy.name}
      </DrawerHeader>
      <DrawerContent>
        <Box mt={2}>
          {!inWriteMode && (
            <ReadonlyView
              fields={fields}
              data={policy._props}
              onData={() => getReadonlyData(policy)}
            />
          )}

          {inWriteMode && (
            <Form formHandle={formHandle} query={query}>
              <InputControl name="name" label="Name" formHandle={formHandle} autoFocus required />

              <InputControl
                name="description"
                label="Description"
                type="textarea"
                rows={3}
                formHandle={formHandle}
              />

              <DrawerButtons
                submit
                cancel
                isNew={false}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                formHandle={formHandle}
              />
            </Form>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default PolicyDrawer;
